import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
  Font,
} from "@react-pdf/renderer";
import { formatLocationsText } from "models/wizard/wizard-modals";

Font.register({
  family: "DM Sans",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/dmsans/v15/rP2Hp2ywxg089UriCZOIHQ.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://fonts.gstatic.com/s/dmsans/v15/rP2Gp2ywxg089UriCZ2IK3k.ttf",
      fontWeight: "bold",
    },
    {
      src: "https://fonts.gstatic.com/s/dmsans/v15/rP2Hp2ywxg089UriCZ2IHQ.ttf",
      fontStyle: "italic",
    },
    {
      src: "https://fonts.gstatic.com/s/dmsans/v15/rP2Gp2ywxg089UriCZ2IJHk.ttf",
      fontWeight: "bold",
      fontStyle: "italic",
    },
  ],
});

// Helper function to safely extract scroll_downs descriptions
const getScrollDownsText = (scrollDowns: any): string => {
  if (!scrollDowns || scrollDowns.length === 0) return "";
  return scrollDowns
    .map((sd: any) => (typeof sd === "string" ? sd : sd?.description || ""))
    .filter((text: string) => text)
    .join(", ");
};

export async function GeneratePDF(userReport: any) {
  const styles = StyleSheet.create({
    body: {
      fontFamily: "'DM Sans', Arial, sans-serif",
      margin: 40,
      lineHeight: 1.6,
      color: "#333",
    },
    page: {
      padding: 40,
      fontSize: 12,
      lineHeight: 1.5,
    },
    h1: {
      textAlign: "center",
      fontSize: 20,
      marginBottom: 20,
      fontWeight: "bold",
    },
    section: {
      marginBottom: 16,
    },
    sectionTitle: {
      fontSize: 14,
      fontWeight: "bold",
      marginBottom: 8,
      marginTop: 12,
      borderBottomWidth: 1,
      borderBottomColor: "#333",
      paddingBottom: 4,
    },
    sectionContent: {
      marginBottom: 4,
      fontSize: 12,
      lineHeight: 1.4,
    },
    contentItem: {
      marginBottom: 4,
      fontSize: 12,
      lineHeight: 1.4,
    },
    locationLabel: {
      fontWeight: "bold",
      marginBottom: 4,
      fontSize: 12,
    },
    durationText: {
      marginBottom: 4,
      fontSize: 12,
    },
    remarksText: {
      marginBottom: 4,
      fontSize: 12,
    },
    examinationItem: {
      marginBottom: 4,
      fontSize: 12,
      lineHeight: 1.4,
    },
    bulletItem: {
      marginLeft: 12,
      marginBottom: 2,
      fontSize: 12,
    },
    footer: {
      marginTop: 40,
      fontSize: 10,
      textAlign: "center",
      borderTopWidth: 1,
      borderTopColor: "#ccc",
      paddingTop: 10,
      color: "#999",
    },
  });

  const ReportDocument = (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Title */}
        <View style={styles.section}>
          <Text style={styles.h1}>Medical Note</Text>
        </View>

        {/* Complaints Section */}
        {(userReport?.complaints?.length > 0 ||
          userReport?.complaintRemark) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>SUBJECTIVE</Text>
            {userReport?.complaints?.length > 0 && (
              <View>
                {userReport.complaints.map((complaint: any, index: number) => {
                  return (
                    <View key={index}>
                      <Text style={styles.contentItem}>
                        <Text style={{ fontWeight: "bold" }}>
                          {complaint.description}
                        </Text>
                        {complaint.scroll_downs?.length > 0
                          ? ` ${complaint.scroll_downs.join(", ")}`
                          : ""}
                      </Text>
                      {(userReport.duration !== null || (userReport.durationType && userReport.durationType !== "Seconds" && userReport.durationType !== "")) && (
                        <Text style={styles.durationText}>
                          for {userReport.duration ? userReport.duration + " " : ""}{" "}
                          {userReport.duration
                            ? userReport.duration != 1
                              ? userReport.durationType.slice(-1) === "s"
                                ? userReport.durationType
                                : userReport.durationType + "s"
                              : userReport.durationType.slice(-1) === "s"
                              ? userReport.durationType.slice(0, -1)
                              : userReport.durationType
                            : userReport.durationType.toLowerCase()}
                        </Text>
                      )}
                    </View>
                  );
                })}
                {Array.isArray(userReport.complaintLocation) &&
                  userReport.complaintLocation.length > 0 &&
                  Boolean(formatLocationsText(userReport.complaintLocation)) && (
                    <Text style={styles.locationLabel}>
                      Location: {formatLocationsText(userReport.complaintLocation)}
                    </Text>
                  )}
              </View>
            )}
            {userReport?.complaintRemark && (
              <Text style={styles.remarksText}>
                {userReport.complaintRemark}
              </Text>
            )}
          </View>
        )}

        {/* Other Symptoms Section */}
        {(userReport?.otherSymptoms?.length > 0 ||
          userReport?.otherSymptomRemark) && (
          <View style={styles.section}>
            {userReport?.otherSymptoms?.length > 0 && (
              <View>
                {userReport.otherSymptoms.map((item: any, index: number) => {
                  return (
                    <View key={index}>
                      <Text style={styles.contentItem}>
                        {item.description?.trim()}
                        {getScrollDownsText(item.scroll_downs)
                          ? ` ${getScrollDownsText(item.scroll_downs)}`
                          : ""}
                      </Text>
                    </View>
                  );
                })}
                {userReport?.otherSymptoms?.length > 0 &&
                  (userReport?.otherSymptomDuration !== null ||
                    (userReport?.otherSymptomDurationType &&
                      userReport?.otherSymptomDurationType !== "Seconds" &&
                      userReport?.otherSymptomDurationType !== "")) && (
                    <Text style={styles.durationText}>
                      For {userReport.otherSymptomDuration ? userReport.otherSymptomDuration + " " : ""}{" "}
                      {userReport.otherSymptomDuration
                        ? userReport.otherSymptomDuration != 1
                          ? userReport.otherSymptomDurationType.slice(-1) === "s"
                            ? userReport.otherSymptomDurationType
                            : userReport.otherSymptomDurationType + "s"
                          : userReport.otherSymptomDurationType.slice(-1) === "s"
                          ? userReport.otherSymptomDurationType.slice(0, -1)
                          : userReport.otherSymptomDurationType
                        : userReport.otherSymptomDurationType.toLowerCase()}
                    </Text>
                  )}{userReport?.otherSymptoms?.length > 0 &&
                  Array.isArray(userReport.otherSymptomLocation) &&
                  userReport.otherSymptomLocation.length > 0 &&
                  Boolean(formatLocationsText(userReport.otherSymptomLocation)) && (
                    <Text style={styles.locationLabel}>
                      Location: {formatLocationsText(userReport.otherSymptomLocation)}
                    </Text>
                  )}
              </View>
            )}
            {userReport?.otherSymptomRemark && (
              <Text style={styles.remarksText}>
                {userReport.otherSymptomRemark}
              </Text>
            )}
          </View>
        )}

        {/* History Section */}
        {(userReport?.history?.length > 0 || userReport?.historyRemark) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>HISTORY</Text>
            {userReport?.history?.length > 0 && (
              <View>
                {userReport.history.map((item: any, index: number) => {
                  return (
                    <View key={index}>
                      <Text style={styles.contentItem}>
                        {item.description?.trim()}
                        {getScrollDownsText(item.scroll_downs)
                          ? ` ${getScrollDownsText(item.scroll_downs)}`
                          : ""}
                      </Text>
                    </View>
                  );
                })}
                {userReport?.history?.length > 0 &&
                  (userReport?.historyDuration !== null ||
                    (userReport?.historyDurationType &&
                      userReport?.historyDurationType !== "Seconds" &&
                      userReport?.historyDurationType !== "")) && (
                    <Text style={styles.durationText}>
                      For {userReport.historyDuration ? userReport.historyDuration + " " : ""}{" "}
                      {userReport.historyDuration
                        ? userReport.historyDuration != 1
                          ? userReport.historyDurationType.slice(-1) === "s"
                            ? userReport.historyDurationType
                            : userReport.historyDurationType + "s"
                          : userReport.historyDurationType.slice(-1) === "s"
                          ? userReport.historyDurationType.slice(0, -1)
                          : userReport.historyDurationType
                        : userReport.historyDurationType.toLowerCase()}
                    </Text>
                  )}</View>
            )}
            {userReport?.historyRemark && (
              <Text style={styles.remarksText}>{userReport.historyRemark}</Text>
            )}
          </View>
        )}

        {/* Examination Section */}
        {(userReport?.examinations?.length > 0 ||
          userReport?.examinationRemark) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>ON EXAMINATION</Text>
            {userReport?.examinations?.length > 0 && (
              <View>
                {handleExaminationPDF(userReport.examinations, styles)}
                {Array.isArray(userReport.examinationLocation) &&
                  userReport.examinationLocation.length > 0 &&
                  Boolean(formatLocationsText(userReport.examinationLocation)) && (
                    <Text style={styles.locationLabel}>
                      Location: {formatLocationsText(userReport.examinationLocation)}
                    </Text>
                  )}
              </View>
            )}
            {userReport?.examinationRemark && (
              <Text style={styles.remarksText}>
                {userReport.examinationRemark}
              </Text>
            )}
          </View>
        )}

        {/* Assessment Section */}
        {(() => {
          const getAssessmentLocations = (rep: any): any[] => {
            if (!rep) return [];
            const candidates = [
              rep.impressionLocation,
              rep.assessmentLocation,
              rep.impression_location,
              rep.assessment_location,
              rep.report?.impressionLocation,
              rep.report?.assessmentLocation,
              rep.report?.impression_location,
              rep.report?.assessment_location,
            ];
            for (const c of candidates) {
              if (Array.isArray(c) && c.length > 0) return c;
            }
            return [];
          };
          const assessmentLocations = getAssessmentLocations(userReport);

          return (userReport?.impressions?.length > 0 ||
            userReport?.impressionRemark ||
            assessmentLocations.length > 0) && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>ASSESSMENT</Text>
              <View>
                {userReport?.impressions?.length > 0 &&
                  userReport.impressions.map((item: any, index: number) => {
                    return (
                      <View key={index}>
                        <Text style={styles.contentItem}>
                          {item.description?.trim()}
                          {getScrollDownsText(item.scroll_downs)
                            ? ` ${getScrollDownsText(item.scroll_downs)}`
                            : ""}
                        </Text>
                      </View>
                    );
                  })}
                {assessmentLocations.length > 0 &&
                  Boolean(formatLocationsText(assessmentLocations)) && (
                    <Text style={styles.locationLabel}>
                      Location: {formatLocationsText(assessmentLocations)}
                    </Text>
                  )}
              </View>
              {userReport?.impressionRemark && (
                <Text style={styles.remarksText}>
                  {userReport.impressionRemark}
                </Text>
              )}
            </View>
          );
        })()}

        {/* Treatment/Plan Section */}
        {(userReport?.treatment_plans?.length > 0 ||
          userReport?.treatmentRemark) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>PLAN</Text>
            {userReport?.treatment_plans?.length > 0 && (
              <View>
                {userReport.treatment_plans.map((item: any, index: number) => {
                  return (
                    <View key={index}>
                      <Text style={styles.contentItem}>
                        {item.description?.trim()}
                      </Text>
                      {item.scroll_downs?.length > 0 && (
                        <View>
                          {item.scroll_downs.map(
                            (scroll: string, scrollIndex: number) => (
                              <Text key={scrollIndex} style={styles.bulletItem}>
                                - {scroll}
                              </Text>
                            )
                          )}
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            )}
            {userReport?.treatmentRemark && (
              <Text style={styles.remarksText}>
                {userReport.treatmentRemark}
              </Text>
            )}
          </View>
        )}
      </Page>
    </Document>
  );

  const pdfBlob = await pdf(ReportDocument).toBlob();
  return pdfBlob;
}

function handleExaminationPDF(examination: any[], styles: any) {
  if (!examination || examination.length === 0) return null;

  const vitalsOrder = [
    "Temperature",
    "Blood Pressure",
    "Heart Rate",
    "Height & Weight",
    "Body Mass Index",
  ];

  const sortedExaminations = [...examination].sort((a: any, b: any) => {
    const aIsVital = vitalsOrder.includes(a.description);
    const bIsVital = vitalsOrder.includes(b.description);
    if (aIsVital && !bIsVital) return -1;
    if (!aIsVital && bIsVital) return 1;

    const aHasId = "_id" in a && a._id;
    const bHasId = "_id" in b && b._id;
    return aHasId ? 1 : bHasId ? -1 : 0;
  });

  return sortedExaminations.map((item: any, index: number) => {
    const scrollDownsText = getScrollDownsText(item.scroll_downs);
    const hasConditions = scrollDownsText.length > 0;

    let weightKG, weightPounds, heightFeets, heightInches, heightCM, tempUnit;

    if (item.description === "Height & Weight") {
      weightKG = item.value?.find((x: any) => x.unitValue === "Kilos");
      weightPounds = item.value?.find((y: any) => y.unitValue === "Pounds");
      heightCM = item.value?.find((y: any) => y.unitValue === "Centimeters");
      heightFeets = item.value?.find((y: any) => y.unitValue === "Feets");
      heightInches = item.value?.find((y: any) => y.unitValue === "Inches");
    }

    if (item.description === "Temperature" && item.value?.[0]?.unitValue) {
      tempUnit = item.value[0].unitValue === "Fahrenheit" ? "F°" : "C°";
    }

    const isStaticExamination =
      item.description === "Temperature" ||
      item.description === "Blood Pressure" ||
      item.description === "Heart Rate" ||
      item.description === "Height & Weight" ||
      item.description === "Body Mass Index";

    return (
      <View key={item?._id || index} style={{ marginBottom: 4 }}>
        {/* Height & Weight (KG) */}
        {weightKG && (
          <Text style={styles.examinationItem}>
            Height: {heightCM?.inputValue} centimeters, Weight:{" "}
            {weightKG?.inputValue} kilos
            {hasConditions ? ` ${scrollDownsText}` : ""}
          </Text>
        )}

        {/* Height & Weight (Pounds) */}
        {weightPounds && (
          <Text style={styles.examinationItem}>
            Height: {heightFeets?.inputValue} feets {heightInches?.inputValue}{" "}
            inches, Weight: {weightPounds?.inputValue} pounds
            {hasConditions ? ` ${scrollDownsText}` : ""}
          </Text>
        )}

        {/* Other examinations */}
        {!weightKG && !weightPounds && (
          <Text style={styles.examinationItem}>
            {item.description}
            {isStaticExamination ? ": " : " "}
            {item.value?.[0]?.inputValue}
            {item.description === "Temperature"
              ? tempUnit
              : item.value?.[0]?.unitValue
              ? ` ${item.value[0].unitValue}`
              : ""}
            {hasConditions ? ` ${scrollDownsText}` : ""}
          </Text>
        )}
      </View>
    );
  });
}
